import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBell, FaTimes, FaCheck, FaEdit, FaNewspaper, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Polling pour les nouvelles notifications (toutes les 30 secondes)
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/articles/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Erreur notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/articles/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/articles/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'article_refused': <FaTimes className="text-red-500" />,
      'article_corrections_needed': <FaEdit className="text-orange-500" />,
      'article_approved': <FaCheck className="text-green-500" />,
      'article_published': <FaNewspaper className="text-blue-600" />,
      'reminder_j_3': <FaClock className="text-blue-500" />,
      'reminder_j_1': <FaExclamationTriangle className="text-orange-500" />,
      'reminder_j0': <FaExclamationTriangle className="text-red-500" />,
      'reminder_j_plus_1': <FaTimes className="text-red-600" />
    };
    return icons[type] || <FaBell className="text-gray-500" />;
  };

  const getNotificationStyle = (type) => {
    const styles = {
      'article_refused': 'border-l-4 border-red-500 bg-red-50',
      'article_corrections_needed': 'border-l-4 border-orange-500 bg-orange-50',
      'article_approved': 'border-l-4 border-green-500 bg-green-50',
      'article_published': 'border-l-4 border-blue-600 bg-blue-50',
      'reminder_j_3': 'border-l-4 border-blue-500 bg-blue-50',
      'reminder_j_1': 'border-l-4 border-orange-500 bg-orange-50',
      'reminder_j0': 'border-l-4 border-red-500 bg-red-50',
      'reminder_j_plus_1': 'border-l-4 border-red-600 bg-red-100'
    };
    return styles[type] || 'border-l-4 border-gray-300 bg-gray-50';
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    if (notification.articleId) {
      navigate('/student/articles');
    }
    if (notification.publicUrl) {
      window.open(notification.publicUrl, '_blank');
    }
    
    setIsOpen(false);
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = (now - date) / 1000 / 60; // minutes
    
    if (diff < 1) return 'À l\'instant';
    if (diff < 60) return `Il y a ${Math.floor(diff)} min`;
    if (diff < 1440) return `Il y a ${Math.floor(diff / 60)} h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
      >
        <FaBell className="text-xl text-gray-600" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl z-50 overflow-hidden border"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-4 flex items-center justify-between">
                <h3 className="font-bold">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-blue-200 hover:text-white"
                  >
                    Tout marquer comme lu
                  </button>
                )}
              </div>

              {/* Notifications list */}
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <FaBell className="text-4xl mx-auto mb-3 text-gray-300" />
                    <p>Aucune notification</p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !notif.read ? getNotificationStyle(notif.type) : 'opacity-60'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-xl mt-1">
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold text-sm ${!notif.read ? 'text-gray-900' : 'text-gray-600'}`}>
                            {notif.title}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            {notif.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTime(notif.createdAt)}
                          </p>
                        </div>
                        {!notif.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="border-t p-3 text-center">
                  <button
                    onClick={() => {
                      navigate('/student/articles');
                      setIsOpen(false);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Voir tous mes articles →
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
